// client/src/components/PromoteWizard.jsx
// "מעבר לשנה חדשה" — copy the whole roster (cards included) into next year's
// camp, bumping each class one grade up, then sever the cards from this camp.
//
// Three steps, modeled on PointsImportModal's machine:
//   'target' — pick the destination camp (from the camps the user manages)
//   'review' — the server's analyze report: per-class mapping, per-problem-class
//              keep/none decisions, conflicts (blocking), same-name warnings
//   'done'   — written counts + link to the target camp
//
// The server re-validates on commit; if the commit round returns blocked
// (roster drifted since analyze) we stay on review with the fresh report.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from './Modal.jsx';
import { useCamps, usePromoteCamp } from '../hooks/useCamps.js';
import { AcademicCapIcon, ExclamationTriangleIcon } from './Icons.jsx';

export default function PromoteWizard({ open, onClose, campId, campName, user }) {
  const { analyze, commit } = usePromoteCamp(campId);
  const camps = useCamps();
  const [step, setStep] = useState('target'); // 'target' | 'review' | 'done'
  const [targetId, setTargetId] = useState('');
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [result, setResult] = useState(null);

  function reset() {
    setStep('target');
    setTargetId('');
    setDecisions({});
    setBusy(false);
    setError(null);
    setReport(null);
    setResult(null);
  }
  function close() {
    reset();
    onClose();
  }

  async function run(fn, body, onOk) {
    setBusy(true);
    setError(null);
    try {
      onOk(await fn(body));
    } catch (e) {
      setError(humanizeError(e));
    } finally {
      setBusy(false);
    }
  }

  const targets = (camps.data || []).filter((c) => c.id !== campId);

  return (
    <Modal open={open} onClose={close} title="מעבר לשנה חדשה" maxWidth="max-w-2xl">
      {step === 'target' && (
        <TargetStep
          campName={campName}
          targets={targets}
          targetId={targetId}
          setTargetId={setTargetId}
          isSuperAdmin={!!user?.isSuperAdmin}
          busy={busy}
          error={error}
          onCancel={close}
          onNext={() =>
            run(analyze, { targetCampId: targetId, classDecisions: {} }, (r) => {
              setDecisions({});
              setReport(r);
              setStep('review');
            })
          }
        />
      )}

      {step === 'review' && report && (
        <ReviewStep
          report={report}
          decisions={decisions}
          setDecisions={setDecisions}
          busy={busy}
          error={error}
          onBack={() => { setError(null); setStep('target'); }}
          onConfirm={() =>
            run(commit, { targetCampId: targetId, classDecisions: decisions }, (r) => {
              if (r.blocked) {
                // Roster drifted between analyze and commit — show the fresh report.
                setReport(r);
                setError('הנתונים השתנו מאז הבדיקה — בדקו את הדוח המעודכן ונסו שוב.');
              } else {
                setResult(r);
                setStep('done');
              }
            })
          }
        />
      )}

      {step === 'done' && result && <DoneStep result={result} onClose={close} />}
    </Modal>
  );
}

/* ───────── Step 1: pick the target camp ───────── */

function TargetStep({ campName, targets, targetId, setTargetId, isSuperAdmin, busy, error, onCancel, onNext }) {
  return (
    <div className="space-y-5">
      <div className="card-soft p-4 text-sm text-ink-700 space-y-1.5">
        <p className="font-bold text-brand-900">מה יקרה במעבר?</p>
        <ul className="space-y-1">
          <Bullet>כל התלמידים של <strong>{campName}</strong> יועתקו לתכנית שתבחרו — כולל מספרי הכרטיסים.</Bullet>
          <Bullet>הכיתות יוקפצו אוטומטית (א-1 ← ב-1). כיתות שלא ניתן להקפיץ יוצגו לבחירה בשלב הבא.</Bullet>
          <Bullet>בתכנית הנוכחית הכרטיסים ינותקו; התלמידים וההיסטוריה נשארים לצפייה בדוחות.</Bullet>
        </ul>
      </div>

      {targets.length === 0 ? (
        <OfficeNote emphasize />
      ) : (
        <div>
          <div className="label mb-2">לאיזו תכנית להעביר?</div>
          <ul className="space-y-2 max-h-64 overflow-y-auto pe-1">
            {targets.map((c) => (
              <li key={c.id}>
                <label
                  className={`flex items-center gap-3 rounded-2xl border px-4 py-2.5 cursor-pointer transition-colors
                    ${targetId === c.id ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:border-brand-300'}`}
                >
                  <input
                    type="radio"
                    name="promote-target"
                    className="accent-brand-600"
                    checked={targetId === c.id}
                    onChange={() => setTargetId(c.id)}
                  />
                  <span className="font-semibold text-ink-900">{c.name}</span>
                  {c.description && (
                    <span className="text-xs text-ink-500 truncate">{c.description}</span>
                  )}
                </label>
              </li>
            ))}
          </ul>
          {!isSuperAdmin && <OfficeNote />}
        </div>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel}>ביטול</button>
        <button type="button" className="btn-primary" onClick={onNext} disabled={!targetId || busy}>
          {busy ? 'בודק...' : 'בדיקה לפני מעבר'}
        </button>
      </div>
    </div>
  );
}

function OfficeNote({ emphasize = false }) {
  return (
    <div className={`rounded-2xl border px-4 py-3 text-sm ${emphasize ? 'bg-sky-50 border-sky-200 text-sky-900' : 'bg-ink-50 border-ink-200 text-ink-600 mt-3'}`}>
      {emphasize && <p className="font-bold mb-1">אין תכנית זמינה למעבר.</p>}
      לפתיחת תכנית לשנה החדשה יש לפנות למשרד — לאחר שהתכנית תיפתח ותשויך אליכם,
      היא תופיע כאן לבחירה.
    </div>
  );
}

/* ───────── Step 2: review the analyze report ───────── */

const BLOCK_MESSAGE = {
  no_students: 'אין תלמידים בתכנית הנוכחית — אין מה להעביר.',
  conflicts: 'יש התנגשויות מול תכנית היעד — המעבר חסום עד לפתרון.',
};

const STATUS_BADGE = {
  ok: { label: 'תקין', cls: 'bg-mint-100 text-mint-800 border-mint-200' },
  graduating: { label: 'בוגרים (יב)', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  unparsable: { label: 'שם לא מזוהה', cls: 'bg-joy-100 text-joy-800 border-joy-200' },
};

function ReviewStep({ report, decisions, setDecisions, busy, error, onBack, onConfirm }) {
  const { summary, mapping, conflicts, warnings, noClassCount } = report;
  const problemRows = mapping.filter((m) => m.status !== 'ok');
  const undecided = problemRows.filter((m) => !decisions[m.from]).length;
  const hasConflicts = summary.conflictCount > 0;
  const canCommit = !hasConflicts && undecided === 0 && summary.total > 0;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-700">
        מעבר מ-<strong>{report.source.name}</strong> אל <strong>{report.target.name}</strong>
        {report.target.studentCount > 0 && (
          <span className="text-xs text-ink-500"> (בתכנית היעד כבר יש {report.target.studentCount} תלמידים)</span>
        )}
      </p>

      <SummaryCards summary={summary} noClassCount={noClassCount} />

      {report.blockReason === 'no_students' && <ErrorBanner>{BLOCK_MESSAGE.no_students}</ErrorBanner>}

      {mapping.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-4 py-2.5 bg-brand-50 border-b border-brand-100 text-sm font-bold text-brand-900">
            הקפצת כיתות
          </div>
          <ul className="divide-y divide-ink-100 max-h-72 overflow-y-auto">
            {mapping.map((m) => (
              <li key={m.from} className="px-4 py-2.5 text-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-ink-900">{m.from}</span>
                  {m.status === 'ok' && (
                    <>
                      <ArrowStart />
                      <span className="font-semibold text-brand-800">{m.to}</span>
                    </>
                  )}
                  <span className="text-xs text-ink-500 num">({m.count} תלמידים)</span>
                  <span className={`badge border text-[10px] ${STATUS_BADGE[m.status].cls}`}>
                    {STATUS_BADGE[m.status].label}
                  </span>
                </div>
                {m.status !== 'ok' && (
                  <div className="mt-2 flex flex-col sm:flex-row gap-2">
                    <DecisionRadio
                      name={`dec-${m.from}`}
                      checked={decisions[m.from] === 'keep'}
                      onChange={() => setDecisions((d) => ({ ...d, [m.from]: 'keep' }))}
                      label={`להשאיר בשם "${m.from}"`}
                    />
                    <DecisionRadio
                      name={`dec-${m.from}`}
                      checked={decisions[m.from] === 'none'}
                      onChange={() => setDecisions((d) => ({ ...d, [m.from]: 'none' }))}
                      label="להעתיק ללא כיתה"
                    />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {undecided > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 text-sm font-medium">
          יש {undecided} כיתות שדורשות הכרעה — בחרו לכל אחת מה לעשות כדי להמשיך.
        </div>
      )}

      {hasConflicts && (
        <>
          <ErrorBanner>{BLOCK_MESSAGE.conflicts}</ErrorBanner>
          <ConflictsTable conflicts={conflicts} total={summary.conflictCount} />
        </>
      )}

      {!hasConflicts && warnings?.length > 0 && (
        <div className="rounded-2xl bg-sky-50 border border-sky-200 text-sky-900 px-4 py-3 text-sm">
          <p className="font-bold">שימו לב — שמות שכבר קיימים בתכנית היעד:</p>
          <p className="mt-1 text-xs">
            {warnings.slice(0, 8).map((w) => w.name).join(' · ')}
            {warnings.length > 8 && ` ועוד ${warnings.length - 8}`}
          </p>
          <p className="mt-1 text-xs">ייתכן שהמעבר כבר בוצע בעבר — התלמידים האלו יועתקו שוב.</p>
        </div>
      )}

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onBack} disabled={busy}>חזרה</button>
        <button type="button" className="btn-primary" onClick={onConfirm} disabled={!canCommit || busy}>
          {busy ? 'מעביר...' : `העבר ${summary.total} תלמידים`}
        </button>
      </div>
    </div>
  );
}

function DecisionRadio({ name, checked, onChange, label }) {
  return (
    <label
      className={`flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs cursor-pointer transition-colors
        ${checked ? 'border-brand-500 bg-brand-50 font-semibold text-brand-900' : 'border-ink-200 text-ink-600 hover:border-brand-300'}`}
    >
      <input type="radio" name={name} className="accent-brand-600" checked={checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
}

function ConflictsTable({ conflicts, total }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-2.5 bg-joy-50 border-b border-joy-200 text-sm font-bold text-joy-900 num">
        התנגשויות ({total})
      </div>
      <ul className="divide-y divide-ink-100 max-h-56 overflow-y-auto">
        {conflicts.map((c, i) => (
          <li key={i} className="px-4 py-2 text-sm flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ink-900">{c.name}</span>
            <span className="text-xs text-joy-700">
              {c.field === 'cardNumber' ? 'מספר הכרטיס כבר תפוס בתכנית היעד' : 'המזהה החיצוני כבר תפוס בתכנית היעד'}
            </span>
            <span className="text-xs text-ink-400 num" dir="ltr">{c.value}</span>
          </li>
        ))}
        {total > conflicts.length && (
          <li className="px-4 py-2 text-xs text-ink-500 num">ועוד {total - conflicts.length} התנגשויות...</li>
        )}
      </ul>
    </div>
  );
}

function SummaryCards({ summary, noClassCount }) {
  const cards = [
    { label: 'תלמידים למעבר', value: summary.total, tone: 'ink' },
    { label: 'עם כרטיס', value: summary.withCard, tone: 'mint' },
    { label: 'כיתות מוקפצות', value: summary.mappedClasses, tone: 'sky' },
    {
      label: 'דורשות הכרעה',
      value: summary.problemClasses,
      tone: summary.problemClasses > 0 ? 'joy' : 'ink',
    },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-2xl border p-3 text-center ${STAT_TONE[c.tone]}`}>
            <div className="text-2xl font-extrabold num">{c.value}</div>
            <div className="text-[11px] font-bold mt-0.5">{c.label}</div>
          </div>
        ))}
      </div>
      {noClassCount > 0 && (
        <p className="mt-1.5 text-xs text-ink-500 num">{noClassCount} תלמידים ללא כיתה יועתקו כפי שהם.</p>
      )}
    </div>
  );
}

const STAT_TONE = {
  ink: 'bg-white border-ink-200 text-ink-800',
  mint: 'bg-mint-50 border-mint-200 text-mint-800',
  sky: 'bg-sky-50 border-sky-200 text-sky-800',
  joy: 'bg-joy-50 border-joy-200 text-joy-800',
};

/* ───────── Step 3: done ───────── */

function DoneStep({ result, onClose }) {
  const { written, target } = result;
  return (
    <div className="space-y-4">
      <div className="card p-5 bg-mint-50 border-mint-200 text-mint-900 flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-mint-500 text-white grid place-items-center shrink-0">
          <AcademicCapIcon className="w-6 h-6" />
        </div>
        <div>
          <strong className="block text-lg">המעבר הושלם</strong>
          <span className="text-sm num">
            הועברו {written.students} תלמידים אל {target.name}
            {written.classes > 0 && ` · נוצרו ${written.classes} כיתות`}
            {written.cardsCleared > 0 && ` · נותקו ${written.cardsCleared} כרטיסים מהתכנית הנוכחית`}
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onClose}>סגור</button>
        <Link to={`/camps/${target.id}/students`} className="btn-primary" onClick={onClose}>
          מעבר לתכנית החדשה
        </Link>
      </div>
    </div>
  );
}

/* ───────── Bits ───────── */

function Bullet({ children }) {
  return (
    <li className="flex items-start gap-2">
      <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0 mt-1.5" />
      <span>{children}</span>
    </li>
  );
}

// "from ← to" arrow pointing at the target name (RTL: points start-ward).
function ArrowStart() {
  return (
    <svg className="w-4 h-4 text-ink-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5m0 0l6-6m-6 6l6 6" />
    </svg>
  );
}

function ErrorBanner({ children }) {
  return (
    <div className="rounded-2xl bg-joy-50 border border-joy-200 text-joy-900 px-4 py-3 text-sm font-medium flex items-start gap-2">
      <ExclamationTriangleIcon className="w-5 h-5 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function humanizeError(e) {
  const code = e?.code || '';
  const msg = e?.message || '';
  if (msg === 'same_camp') return 'לא ניתן להעביר תכנית לעצמה.';
  if (msg === 'not_target_camp_manager') return 'אין לך הרשאה על תכנית היעד.';
  if (e?.status === 403) return 'אין לך הרשאה לפעולה זו.';
  if (e?.status === 401) return 'הסשן פג. רענן את הדף והיכנס מחדש.';
  if (e?.status === 404) return 'התכנית לא נמצאה.';
  if (e?.status >= 500) return `שגיאת שרת (${e.status}). נסה שוב בעוד רגע.`;
  return `שגיאה: ${msg || code || 'לא צפויה'}`;
}
